<template>
  <v-navigation-drawer
    v-model="open"
    temporary
    fixed
    right
    stateless
    :clipped="false"
    width="400px"
  >
    <div>
      <v-list-item class="position-relative">
        <v-list-item-content>
          <v-list-item-title class="d-flex align-center">
            <v-icon class="mr-3">$contacts</v-icon>
            <div class="flex-fill">{{ entity.name }} Contacts</div>
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-on="on"
                  v-bind="attrs"
                  icon
                  small
                  @click="open = false"
                  :disabled="loading"
                >
                  <v-icon>$close</v-icon>
                </v-btn>
              </template>
              <span>Close</span>
            </v-tooltip>
          </v-list-item-title>
        </v-list-item-content>
        <v-progress-linear
          :active="loading"
          :indeterminate="loading"
          absolute
          bottom
        ></v-progress-linear>
      </v-list-item>

      <v-divider></v-divider>
      <div class="flex-fill">
        <v-scroll-y-transition mode="out-in">
          <v-list v-if="!contact" two-line>
            <template v-if="entity.contacts && entity.contacts.length > 0">
              <template v-for="(contact, i) in entity.contacts">
                <v-list-item :key="i" link @click="editContact(contact)">
                  <v-list-item-avatar>
                    <font-awesome-icon
                      class="text-h4 grey--text darken-2"
                      icon="user-circle"
                    ></font-awesome-icon>
                  </v-list-item-avatar>

                  <v-list-item-content>
                    <v-list-item-title
                      >{{ contact.name }} -
                      <span
                        class="secondary--text lighten-text-2 text-capitalize"
                        >{{ contact.contactType }}</span
                      ></v-list-item-title
                    >
                    <v-list-item-subtitle>{{
                      contact.email
                    }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>
            </template>
            <v-list-item v-else>
              <v-list-item-content>
                <div class="text-center grey--text">
                  No Contacts Found.
                  <a href="#" @click.prevent="editContact()">Add Contact</a>
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="pa-4">
            <div>
              <v-btn
                text
                color="primary"
                @click="contact = null"
                :disabled="loading"
              >
                <v-icon class="mr-2">$prev</v-icon> Back
              </v-btn>
            </div>
            <div class="text-center mt-4">
              <font-awesome-icon
                icon="user-circle"
                class="text-h1 grey--text"
              ></font-awesome-icon>

              <div class="text-h4 grey--text text-darken-4 mt-2">
                {{ isNew ? "Add" : "Edit" }} Contact
              </div>
            </div>

            <ValidationObserver
              ref="observer"
              tag="form"
              @submit.prevent="onSubmit"
            >
              <v-row class="mt-4">
                <v-col cols="12">
                  <ValidationProvider
                    rules="required|min:2|max:200"
                    v-slot="{ errors }"
                    vid="Name"
                  >
                    <v-text-field
                      v-model="contact.name"
                      :autofocus="isNew"
                      label="Name*"
                      outlined
                      :error-messages="errors"
                      :disabled="loading"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <ValidationProvider
                    :rules="{
                      required: true,
                      email: true,
                      max: 255,
                      notTaken: contactEmails.filter(
                        (x) => x !== contact.originalEmail
                      ),
                    }"
                    v-slot="{ errors }"
                    vid="Email"
                  >
                    <v-text-field
                      v-model="contact.email"
                      label="Email*"
                      outlined
                      :error-messages="errors"
                      :disabled="loading"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <ValidationProvider
                    rules="required|min:2|max:255"
                    v-slot="{ errors }"
                    vid="ContactType"
                  >
                    <v-select
                      v-model="contact.contactType"
                      label="Type*"
                      outlined
                      :items="contactTypes"
                      :error-messages="errors"
                      :disabled="loading"
                    ></v-select>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </ValidationObserver>
          </div>
        </v-scroll-y-transition>
      </div>
    </div>
    <template v-slot:append>
      <div>
        <v-divider></v-divider>
        <div v-if="!contact" class="pt-2 px-4 d-flex">
          <v-btn
            color="green darken-1 white--text mb-2"
            elevation="0"
            @click="editContact()"
          >
            Add Contact
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text elevation="0" @click="open = false"> Close </v-btn>
        </div>
        <div v-else class="py-2 px-4 d-flex">
          <v-btn
            v-if="!isNew"
            color="red darken-1 white--text"
            elevation="0"
            @click="openConfirm = true"
            :disabled="loading"
          >
            Delete
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text elevation="0" @click="contact = null" :disabled="loading">
            Back
          </v-btn>
          <v-btn
            color="primary"
            elevation="0"
            @click="onSubmit"
            :loading="loading"
          >
            Save
          </v-btn>
        </div>
      </div>
    </template>

    <v-dialog v-model="openConfirm" max-width="400px">
      <v-card>
        <dialog-title
          v-model="openConfirm"
          title="Confirm Delete"
          :disabled="loading"
        ></dialog-title>
        <v-card-text v-if="contact">
          <v-row>
            <v-col cols="12">
              <p class="text-body-1">
                You are about to delete:
                <strong class="red--text darken-1 font-weight-bold">
                  {{ contact.name }}.
                </strong>
                <br /><br />
                Are you sure?
              </p>
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                :disabled="loading"
                text
                color="primary"
                @click="openConfirm = false"
              >
                Cancel
              </v-btn>
              <v-btn
                :loading="loading"
                color="red darken-1"
                dark
                @click="OnConfirmDelete()"
              >
                Yes, delete contact
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import Vue from "vue";
import entityService from "../../services/entityService";

import DialogTitle from "../../components/DialogTitle.vue";

const newContact = () => ({
  contactType: null,
  name: null,
  email: null,
  primaryInd: false,
});

export default {
  name: "EntityContacts",
  components: { DialogTitle },
  data: () => ({
    open: false,
    openConfirm: false,
    entity: {},
    entityPath: null,
    contact: null,
    pendingStateChanges: 0,
    isNew: false,
  }),
  computed: {
    tenantId() {
      return this.$store.state.tenantId;
    },
    contactTypes() {
      return this.$store.getters["tenants/tenant"].contactTypes;
    },
    loading() {
      return this.pendingStateChanges > 0;
    },
    contactEmails() {
      return Array.isArray(this.entity.contacts)
        ? this.entity.contacts.map((x) => x.email)
        : [];
    },
  },
  watch: {
    open(val, prev) {
      if (val !== prev) {
        if (!val) this.reset();
      }
    },
  },
  methods: {
    showContacts(entity, path) {
      this.entity = entity;
      this.entityPath = path;
      this.open = true;
    },
    editContact(contact) {
      this.contact = contact
        ? JSON.parse(JSON.stringify(contact))
        : newContact();

      this.contact.originalEmail = this.contact.email + "";

      this.isNew = !contact;
    },
    async onSubmit() {
      let isValid = this.$refs.observer.validate();
      if (!isValid) return;

      // prep api data
      let data = JSON.parse(JSON.stringify(this.entity));
      let contact = JSON.parse(JSON.stringify(this.contact));

      // identify entity contact index
      let targetIndex = data.contacts.findIndex(
        (x) => x.email === contact.originalEmail
      );

      // upsert api data
      if (targetIndex == -1) data.contacts.push(contact);
      else data.contacts[targetIndex] = contact;

      // send request
      this.pendingStateChanges++;

      entityService
        .upsert(this.tenantId, this.entityPath, data)
        .then(
          () => {
            // upsert client data
            if (targetIndex == -1) this.entity.contacts.push(contact);
            else Vue.set(this.entity.contacts, targetIndex, contact);

            // reset contact form
            this.contact = null;

            // success message
            this.$store.dispatch(
              "notifications/success",
              `Successfully updated contacts.`
            );
          },
          (err) => {
            console.warn(err);

            if (err.statusCode === 400) {
              this.$refs.observer.setErrors(err.result);
            } else {
              console.warn(err);
              this.$store.dispatch(
                "notifications/error",
                `An unexpected error occurred while trying to ${
                  this.isNew ? "add" : "update"
                } contact. Please try again later.`
              );
            }
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    async OnConfirmDelete() {
      let isValid = this.$refs.observer.validate();
      if (!isValid) return;

      // prep api data
      let data = JSON.parse(JSON.stringify(this.entity));
      let contact = JSON.parse(JSON.stringify(this.contact));

      // identify entity contact index
      let targetIndex = data.contacts.findIndex(
        (x) => x.email === contact.originalEmail
      );

      // remove from api data
      data.contacts.splice(targetIndex, 1);

      // send request
      this.pendingStateChanges++;

      entityService
        .upsert(this.tenantId, this.entityPath, data)
        .then(
          () => {
            // remove from client data
            this.entity.contacts.splice(targetIndex, 1);

            // reset contact form
            this.contact = null;
            this.openConfirm = false;

            // success message
            this.$store.dispatch(
              "notifications/success",
              `Successfully removed contact.`
            );
          },
          (err) => {
            console.warn(err);

            if (err.statusCode === 400) {
              this.$refs.observer.setErrors(err.result);
            } else {
              console.warn(err);
              this.$store.dispatch(
                "notifications/error",
                `An unexpected error occurred while trying to remove contact. Please try again later.`
              );
            }
          }
        )
        .finally(() => this.pendingStateChanges--);
    },
    reset() {
      this.contact = null;
      this.entity = {};
      this.entityPath = null;
      this.$refs.observer?.reset();
    },
    close() {
      this.open = false;
      this.reset();
    },
  },
};
</script>